// Подписи двух новых инженерных разделов: «Батарейки и сроки» и «Автоверификации».
//
// ЗАЧЕМ ОТДЕЛЬНЫМ ФАЙЛОМ, А НЕ В lib/p73.ts. Там лежат КОПИИ бланков портала 73 (подписи
// строк актов, этапы отчётов) — то есть чужие слова, которые мы обязаны повторять дословно.
// Здесь наши собственные: названия состояний срока, пороги словами, разрезы фильтра.
// Смешивать их значит однажды «поправить формулировку» в копии бланка и молча разойтись
// с порталом.

/** Адреса разделов. Ровно эти пути нужно добавить маршрутами в App.tsx. */
export const ПУТИ_СРОКОВ = {
  сроки: '/equip-due',
  автоверификации: '/auto-verifications',
} as const

export const РАЗМЕР_СТРАНИЦЫ = 50   // как PAGE в p73_equip_due.py

// ─────────── состояния срока ───────────
// Семь, а не три. Три («просрочено / скоро / в порядке») врали бы в четырёх случаях из
// семи: дата уже случившегося события, неразборчивая подпись, значение без даты вовсе
// («H20E» вместо «H20E, 12.2027») и дата дальше года. Каждое из них надо назвать своим
// именем, иначе экран выдаст за просрочку то, что просрочкой не является.

export interface ВидСостояния {
  /** Как это называется на экране. */
  название: string
  /** Класс бейджа из styles.css: lose — красный, won — зелёный, пусто — серый. */
  бейдж: '' | 'won' | 'lose'
  /** Что это значит. Идёт в подсказку и в блок «как это считано». */
  смысл: string
}

export const СОСТОЯНИЯ: Record<string, ВидСостояния> = {
  overdue: {
    название: 'просрочено',
    бейдж: 'lose',
    смысл: 'срок из последнего акта уже прошёл',
  },
  soon: {
    название: 'скоро',
    бейдж: 'lose',
    смысл: 'срок истекает в ближайшие месяцы',
  },
  year: {
    название: 'в течение года',
    бейдж: '',
    смысл: 'срок истекает в пределах года',
  },
  ok: {
    название: 'в порядке',
    бейдж: 'won',
    смысл: 'срок дальше года',
  },
  fact: {
    название: 'дата события',
    бейдж: '',
    смысл: 'это дата уже случившегося (год установки, последняя проверка), а не срок — '
      + 'просрочкой она не бывает',
  },
  unclear: {
    название: 'неясно',
    бейдж: '',
    смысл: 'подпись в акте не говорит, срок это или дата уже случившегося; '
      + 'такие строки в просрочку намеренно не попадают',
  },
  nodate: {
    название: 'без даты',
    бейдж: '',
    смысл: 'в поле акта записано что-то другое — например тип гидростата «H20E» '
      + 'без срока годности рядом',
  },
}

export const названиеСостояния = (к?: string | null) =>
  (к && СОСТОЯНИЯ[к]?.название) || к || '—'

export const бейджСостояния = (к?: string | null): '' | 'won' | 'lose' =>
  (к && СОСТОЯНИЯ[к]?.бейдж) || ''

/** Порядок состояний на экране — тот же, что в сортировке ядра (ВЕС_СОСТОЯНИЯ). */
export const ПОРЯДОК_СОСТОЯНИЙ = [
  'overdue', 'soon', 'year', 'ok', 'unclear', 'fact', 'nodate',
] as const

/** Состояния, которые требуют действия. По ним и открывают раздел. */
export const ГОРЯЧИЕ = ['overdue', 'soon'] as const

// ─────────── точность даты ───────────
// В бланке инженер пишет то, что напечатано на батарее, а там обычно месяц. Показать
// «30.06.2029» вместо «06.2029» значит придумать день, которого никто не называл.

export const ТОЧНОСТЬ: Record<string, string> = {
  day: 'день',
  month: 'месяц',
  year: 'год',
}

/**
 * Срок так, как его записал инженер, плюс разобранная дата — но только если разбор
 * что-то ДОБАВИЛ. У «24.03.2023» добавлять нечего, у «H 20E, 12.2027» — есть что.
 */
export function срокСловами(значение: string, due: string | null, точность?: string | null): string {
  if (!due) return значение || '—'
  const [г, м, д] = due.split('-')
  const разобрано = точность === 'year' ? г : точность === 'month' ? `${м}.${г}` : `${д}.${м}.${г}`
  const чистое = (значение || '').trim()
  return чистое === разобрано ? чистое : `${чистое} → ${разобрано}`
}

/**
 * «просрочен на 2 года 3 мес» / «через 4 мес». Дни в месяцы переводим приблизительно и
 * говорим об этом словом «около» там, где точности нет: у срока, известного до месяца,
 * «через 118 дней» — выдуманная точность.
 */
export function осталосьСловами(дней: number | null | undefined, точность?: string | null): string {
  if (дней === null || дней === undefined) return '—'
  const модуль = Math.abs(дней)
  const точно = точность === 'day'
  let текст: string
  if (модуль < 45 && точно) {
    текст = `${модуль} дн.`
  } else if (модуль < 365) {
    текст = `${Math.max(1, Math.round(модуль / 30))} мес.`
  } else {
    const лет = Math.floor(модуль / 365)
    const мес = Math.round((модуль - лет * 365) / 30)
    текст = мес ? `${лет} г. ${мес} мес.` : `${лет} г.`
  }
  if (дней < 0) return `просрочен на ${текст}`
  if (дней === 0) return 'истекает сегодня'
  return `через ${текст}`
}

// ─────────── автоверификации ───────────

/**
 * Что это за форма. Единственное, что человек обязан узнать про неё раньше данных: она
 * НИЧЕГО не отправляет в Bitrix. Это не наше ограничение и не незаконченность переноса —
 * так решено на портале 12.06.2026 и записано у него в коде (autover.js:2).
 */
export const ПРО_АВТОВЕРИФИКАЦИИ =
  'Отдельная форма портала 73 для автоматики и электрики. В Bitrix она не уходит вовсе — '
  + 'ни задачи, ни комментария в сделку: так решено на портале 12.06.2026. Поэтому найти '
  + 'эти проверки где-то, кроме портала и этого раздела, нельзя.'
